import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { cloneBookletMutation } from '../../queries/booklet';
import { GET_BOOKLETS_WITH_SERIES } from '../../queries/bookletSeries';
import Modal from '../../common/Modal';

function CloneBookletModal({ booklet, series, zoneId, onClose }) {
  const addMonths = (dateStr, months) => {
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + months);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  // Calculate default dates from series
  const calcDates = () => {
    let buyableFrom = '';
    let buyableTo = '';
    let validFrom = '';
    let validTo = '';

    if (series.booklets && series.booklets.length > 0) {
      const sorted = [...series.booklets].sort((a, b) => new Date(b.validTo || 0) - new Date(a.validTo || 0));
      const lastBooklet = sorted[0];
      const nextDay = new Date(lastBooklet.validTo);
      nextDay.setDate(nextDay.getDate() + 1);
      const startDate = nextDay.toISOString().slice(0, 10);

      validFrom = startDate;
      buyableFrom = startDate;

      if (series.defaultValidityMonths) {
        validTo = addMonths(startDate, series.defaultValidityMonths);
        buyableTo = addMonths(startDate, series.defaultValidityMonths);
      }
    }

    return { buyableFrom, buyableTo, validFrom, validTo };
  };

  const defaults = calcDates();
  const [dates, setDates] = useState(defaults);
  const [error, setError] = useState('');

  const [cloneBooklet, { loading }] = useMutation(cloneBookletMutation, {
    refetchQueries: [{ query: GET_BOOKLETS_WITH_SERIES, variables: { zoneId } }],
    onCompleted: () => onClose(),
    onError: (err) => setError(err.message),
  });

  const couponCount = booklet.couponIds?.length || 0;
  const sponsorCount = booklet.sponsors?.length || 0;
  const categoryCount = booklet.categorySettings?.length || 0;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!dates.buyableFrom || !dates.buyableTo || !dates.validFrom || !dates.validTo) {
      setError('Alla datumfält måste fyllas i.');
      return;
    }

    cloneBooklet({
      variables: {
        _id: booklet._id,
        buyableFrom: dates.buyableFrom,
        buyableTo: dates.buyableTo,
        validFrom: dates.validFrom,
        validTo: dates.validTo,
      },
    });
  };

  const updateDate = (field, value) => {
    setDates((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Modal isOpen closeModal={onClose} className="modal-clone-booklet">
      <h2>Klonar Häfte</h2>

      <div style={{ textAlign: 'left', margin: '1rem 0' }}>
        <p><strong>{booklet.title}</strong></p>
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
          <li>{couponCount} kuponger</li>
          <li>{sponsorCount} sponsorer</li>
          <li>{categoryCount} kategorier</li>
          <li>Pris: {booklet.price} kr</li>
        </ul>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)' }}>
          Allt kopieras från häftet inkl. kuponger och sponsorer. 
        </p>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.85rem', color: 'rgba(0,0,0,0.6)' }}>
          Säljperiod och giltighet sätts baserat på det senaste häftet i serien.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', textAlign: 'left', margin: '1rem 0' }}>
          <label>
            <span style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Säljstart</span>
            <input type="date" value={dates.buyableFrom} onChange={(e) => updateDate('buyableFrom', e.target.value)} disabled={loading} />
          </label>
          <label>
            <span style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Säljslut</span>
            <input type="date" value={dates.buyableTo} onChange={(e) => updateDate('buyableTo', e.target.value)} disabled={loading} />
          </label>
          <label>
            <span style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Giltig från</span>
            <input type="date" value={dates.validFrom} onChange={(e) => updateDate('validFrom', e.target.value)} disabled={loading} />
          </label>
          <label>
            <span style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Giltig till</span>
            <input type="date" value={dates.validTo} onChange={(e) => updateDate('validTo', e.target.value)} disabled={loading} />
          </label>
        </div>

        {error && <p style={{ color: 'red', margin: '0.5rem 0' }}>{error}</p>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button type="button" className="button secondary" onClick={onClose} disabled={loading}>
            Avbryt
          </button>
          <button type="submit" className="button primary" disabled={loading}>
            {loading ? 'Klonar...' : 'Klona'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

CloneBookletModal.propTypes = {
  booklet: PropTypes.object.isRequired,
  series: PropTypes.object.isRequired,
  zoneId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CloneBookletModal;
